/**
 * `?ph` person-hash forwarding for cross-domain CTAs.
 *
 * `ph` is the server-computed pseudonymous person hash (an opaque, salted
 * SHA-256 hex, 64 chars) that email-flows embeds in email links. When a visitor
 * lands on the marketing site from an email, that `?ph` rides on the page URL.
 * Forwarding it onto the outbound booking / membership / quiz links keeps the
 * post-click session on those other domains resolving to the same PostHog
 * person (each app calls `posthog.identify(ph)` via the shared
 * `identifyFromUrlPh`), so an email→…→conversion funnel joins to one person.
 *
 * This only ever *forwards* a hash the browser already holds — it never
 * computes one (no salt, no PII in the bundle), so a crafted `?ph` is no more
 * dangerous here than it is on the landing page itself.
 */

const PERSON_HASH_RE = /^[a-f0-9]{64}$/i

/**
 * Read a valid `ph` person hash from a search string (defaults to the current
 * page's `window.location.search`; no-op / null during SSR). Returns null when
 * absent or not a 64-hex hash.
 */
export function readPersonHash(search?: string): string | null {
  const raw =
    search ?? (typeof window !== "undefined" ? window.location.search : "")
  const ph = new URLSearchParams(raw).get("ph")
  return ph && PERSON_HASH_RE.test(ph) ? ph : null
}

/**
 * Set `ph` on `params` from the given search string (or the current page) when
 * a valid hash is present and `ph` isn't already set. Mutates `params`.
 */
export function applyPersonHash(
  params: URLSearchParams,
  search?: string
): void {
  if (params.has("ph")) return
  const ph = readPersonHash(search)
  if (ph) params.set("ph", ph)
}
